import { Component, Output, EventEmitter, Input, OnChanges, ViewChildren, ElementRef } from '@angular/core';

import {
  Language,
  CategoryDto,
  TeamsWithSupportersDto,
  SupporterFilters,
  SupporterEditDto,
  SupporterForCollectionDto,
  SupporterFiltersCached
} from '../../../shared/model';
import { SharedService } from '../../../shared/shared.service';
import { SchedulerViewService } from '../scheduler-view.service';

import { CheckableSettings, TreeItemLookup } from '@progress/kendo-angular-treeview';

import { of } from 'rxjs';

@Component({
  selector: 'app-filters-bar',
  templateUrl: './filters-bar.component.html',
  styleUrls: ['../../../shared/styles/filters.css',
    '../../../shared/styles/lang-color.css']
})
export class FiltersBarComponent implements OnChanges {
  @Input() allCategories: CategoryDto[];
  @Input() allLanguages: Language[];
  @Input() allTeams: TeamsWithSupportersDto[];

  @ViewChildren('languageInput') languageInput;
  @ViewChildren('categoryInput') categoryInput;

  @Output() changed: EventEmitter<any> = new EventEmitter();

  public checkedKeys: any[] = [];
  public checked = false;
  public enableCheck = true;
  public checkChildren = true;
  public checkParents = true;
  public checkMode: any = 'multiple';
  public selectionMode: any = 'single';
  public expandedKeys: string[] = [];
  public filters: SupporterFilters = <SupporterFilters>{
    SelectedCategory: [],
    SelectedLanguages: [],
    SelectedSupporters: [],
    ShowAvailability: false
  };

  private allExpandedKeys: string[] = [];
  private allCheckedKeys: any[] = [];
  private currentLanguage: Language;
  private currentSupporter: SupporterEditDto;
  private supporters: string[] = [];
  private currentSupporterIndex: number;
  private cachedFilters: SupporterFiltersCached;
  private markAll = false;
  public children = (dataItem: any): any[] => of(dataItem.Supporters) as any;
  public hasChildren = (dataItem: any): boolean => !!dataItem.Supporters;

  constructor(
    private sharedService: SharedService,
    private schedulerViewService: SchedulerViewService) {
    this.currentLanguage = sharedService.getCurrentLanguage();
    this.cachedFilters = sharedService.getCurrentFilters(sharedService.getLoggedUser().Email);
    this.filters.SelectedLanguages.push(this.currentLanguage.Id);
  }

  ngOnChanges(changes) {

    if (!!this.allCategories && !!this.allTeams) {
      this.allTeamsChecked();
      if (!!this.cachedFilters) {
        this.filtersFromCache();
      } else {
        this.initialFilters();
      }
    }
  }

  public get checkableSettings(): CheckableSettings {
    return {
      checkChildren: this.checkChildren,
      checkParents: this.checkParents,
      enabled: this.enableCheck,
      mode: this.checkMode
    };
  }

  handleCheckingTeam(itemLookup: TreeItemLookup): void {
    const checkedIndexes = [... this.checkedKeys];
    const itemWithSupporters = itemLookup.item.dataItem.Supporters !== undefined && itemLookup.item.dataItem.Supporters.length > 0;

    if (itemLookup.children.length > 0 || itemWithSupporters) {
      const checkItem = this.checkedKeys.findIndex(k => k === itemLookup.item.index) < 0;
      const team = this.allTeams.find(t => t.Id === itemLookup.item.dataItem.Id);

      if (checkItem) {
        this.markAllSupportersFromTeam(team);
      }

      this.teamCheckedOrUnchecked(itemLookup, checkedIndexes);
    } else {
      this.supporterCheckedOrUnchecked(itemLookup, checkedIndexes);
    }
  }

  languageFilterChange(event) {
    if (event.target.checked) {
      this.filters.SelectedLanguages.push(event.target.id);
    } else {
      const index = this.filters.SelectedLanguages.findIndex(x => x === event.target.id);
      this.filters.SelectedLanguages.splice(index, 1);
    }
    this.markAll = false;
    this.emitFiltersChange();
  }

  categoryFilterChange(event) {
    if (event.target.checked) {
      this.filters.SelectedCategory.push(event.target.id);
    } else {
      const index = this.filters.SelectedCategory.findIndex(x => x === event.target.id);
      this.filters.SelectedCategory.splice(index, 1);
    }
    this.emitFiltersChange();
  }

  changedSlider() {
    this.filters.ShowAvailability = this.checked;
    this.emitFiltersChange();
  }

  markAllFilters() {
    this.markAll = true;
    this.filters.SelectedSupporters = this.supporters.map(item => {
      return item;
    });
    this.filters.SelectedCategory = this.allCategories.map(item => {
      return item.Id;
    });
    this.filters.SelectedLanguages = this.allLanguages.map(item => {
      return item.Id;
    });

    this.checkedKeys = [... this.allCheckedKeys];
    this.expandedKeys = [... this.allExpandedKeys];
    this.checked = true;
    this.filters.ShowAvailability = this.checked;

    this.languageInput._results.forEach(function (value: ElementRef) {
      value.nativeElement.checked = true;
    });
    this.categoryInput._results.forEach(function (value: ElementRef) {
      value.nativeElement.checked = true;
    });

    this.emitFiltersChange();
  }

  private supporterCheckedOrUnchecked(itemLookup: TreeItemLookup, checkedIndexes: any[]) {
    const checkedOrUncheckedIndex = checkedIndexes.find(x => x === itemLookup.item.index);
    if (checkedOrUncheckedIndex) {
      const index = this.filters.SelectedSupporters.findIndex(x => x === itemLookup.item.dataItem.Id);
      this.filters.SelectedSupporters.splice(index, 1);
      this.emitFiltersChange(null, [itemLookup.item.index]);
    } else {
      this.filters.SelectedSupporters.push(itemLookup.item.dataItem.Id);
      this.emitFiltersChange([itemLookup.item.index]);
    }
  }

  private teamCheckedOrUnchecked(itemLookup: TreeItemLookup, checkedIndexes: any[]) {
    const checkedSupporters = this.filters.SelectedSupporters.map(item => item);
    if (checkedIndexes.findIndex(x => x === itemLookup.item.index) > -1) {
      this.teamChecked(itemLookup);
    } else {
      this.teamUnchecked(itemLookup, checkedSupporters, checkedIndexes);
    }
  }

  private teamChecked(itemLookup: TreeItemLookup) {
    const checkedSupporters: string[] = [];
    const uncheckedKeys: string[] = [];
    uncheckedKeys.push(itemLookup.item.index);

    itemLookup.children.forEach(function (value) {
      if (checkedSupporters.findIndex(x => x === value.item.dataItem.Id) < 0) {
        checkedSupporters.push(value.item.dataItem.Id);
      }
      uncheckedKeys.push(value.item.index);
    });

    const localSupportersFilters = this.filters.SelectedSupporters;

    checkedSupporters.forEach(function (value) {
      const index = localSupportersFilters.findIndex(x => x === value);
      if (index > 0) {
        localSupportersFilters.splice(index, 1);
      }
    });

    this.emitFiltersChange(null, uncheckedKeys);
  }

  private teamUnchecked(itemLookup: TreeItemLookup, checkedSupporters: string[], checkedIndexes: any[]) {
    const checkedKeys: string[] = [];
    checkedKeys.push(itemLookup.item.index);

    itemLookup.children.forEach(function (value) {
      const index = checkedSupporters.findIndex(x => x === value.item.dataItem.Id);
      checkedKeys.push(value.item.index);

      if (checkedIndexes.findIndex(x => x === value.item.index) > -1) {
        checkedSupporters.splice(index, 1);
      } else {
        if (index < 0) {
          checkedSupporters.push(value.item.dataItem.Id);
        }
      }
    });
    this.filters.SelectedSupporters = this.filters.SelectedSupporters.concat(checkedSupporters);

    this.filters.SelectedSupporters = this.filters.SelectedSupporters.filter(function (item, pos, self) {
      return self.indexOf(item) === pos;
    });
    this.emitFiltersChange(checkedKeys);
  }

  private getCurrentUser() {
    const user = this.sharedService.getLoggedUser();
    this.schedulerViewService.getSupporterDetails(user.Email)
      .subscribe(supp => {
        if (supp.Id !== '00000000-0000-0000-0000-000000000000') {
          this.currentSupporter = supp;
          this.filters.SelectedSupporters.push(this.currentSupporter.Id);
          this.filters.SelectedCategory = this.currentSupporter.CategoryIds;
          this.filters.SelectedLanguages = this.currentSupporter.LanguageIds;

          const index = this.allTeams.findIndex(value => this.findSupporter(value.Supporters));
          this.checkedKeys = [];
          this.checkedKeys.push(index + '_' + this.currentSupporterIndex);
          this.expandedKeys.push(index.toString());
          this.changed.emit(this.filters);
        } else {
          this.currentSupporter = null;
          this.filters.SelectedSupporters = this.supporters;
          this.filters.SelectedCategory = this.allCategories.map(item => {
            return item.Id;
          });

          this.changed.emit(this.filters);
        }
      },
        () => {
          this.currentSupporter = null;
          this.filters.SelectedSupporters = this.supporters;
          this.filters.SelectedCategory = this.allCategories.map(item => {
            return item.Id;
          });

          this.emitFiltersChange();
        });
  }

  private findSupporter(supporters: SupporterForCollectionDto[]): boolean {
    const supportersCollection = supporters.filter(x => x.Id === this.currentSupporter.Id);
    if (supportersCollection.length > 0) {
      this.currentSupporterIndex = supporters.findIndex(x => x.Id === this.currentSupporter.Id);
      return true;
    }
    return false;
  }

  isLanguageChecked(id: string): boolean {

    if (this.currentSupporter) {
      return this.currentSupporter.LanguageIds.indexOf(id) !== -1;
    }
    if (this.cachedFilters) {
      return this.cachedFilters.SelectedLanguages.indexOf(id) !== -1;
    }
    if (this.markAll) {
      return true;
    }

    return this.currentLanguage.Id === id;
  }

  isCategoryChecked(id: string) {
    if (this.currentSupporter) {
      return this.currentSupporter.CategoryIds.indexOf(id) !== -1;
    }
    if (this.cachedFilters) {
      return this.cachedFilters.SelectedCategory.indexOf(id) !== -1;
    }
    return true;
  }

  private emitFiltersChange(newCheckedKey?: string[], removedCheckedKey?: string[] ) {
    let tempCheckedKeys = [... this.checkedKeys];
    if (newCheckedKey) {
      tempCheckedKeys = tempCheckedKeys.concat(newCheckedKey);
    }

    if (removedCheckedKey) {
      removedCheckedKey.forEach(function(element) {
        const index = tempCheckedKeys.findIndex(x => x === element);
        tempCheckedKeys.splice(index, 1);
      });
    }

    const supporterFiltersCached = <SupporterFiltersCached>{
      SelectedCategory: this.filters.SelectedCategory,
      SelectedLanguages: this.filters.SelectedLanguages,
      SelectedSupporters: this.filters.SelectedSupporters,
      ShowAvailability: this.filters.ShowAvailability,
      CheckedKeyIndexes: tempCheckedKeys
    };

    this.sharedService.setCurrentFilters(this.sharedService.getLoggedUser().Email, supporterFiltersCached);
    this.changed.emit(this.filters);
  }

  private allTeamsChecked() {
    let teamIndex = 0;
    let supporterIndex = 0;
    const teamCheckboxIndexes = [];
    const localSupporters = [];
    const localExpandedKeys = [];
    this.allTeams.forEach(function (value) {
      teamCheckboxIndexes.push(teamIndex.toString());
      value.Supporters.forEach(function (supporter) {
        teamCheckboxIndexes.push(teamIndex.toString() + '_' + supporterIndex.toString());
        localSupporters.push(supporter.Id);
        supporterIndex++;
      });
      localExpandedKeys.push(teamIndex.toString());
      teamIndex++;
      supporterIndex = 0;
    });

    this.allExpandedKeys = localExpandedKeys;
    this.allCheckedKeys = teamCheckboxIndexes;
    this.supporters = localSupporters;
  }

  private initialFilters() {
    this.checkedKeys = this.allCheckedKeys.map(item => {
      return item;
    });
    this.expandedKeys = this.allExpandedKeys.map(item => {
      return item;
    });
    this.getCurrentUser();
  }

  private filtersFromCache() {
    this.filters = Object.assign(<SupporterFilters>{}, this.cachedFilters);
    this.checkedKeys = this.cachedFilters.CheckedKeyIndexes ? this.cachedFilters.CheckedKeyIndexes : [];
    this.checked = this.filters.ShowAvailability;
    if (this.checkedKeys.length > 0) {
      const matchedKeys = [];
      this.checkedKeys.forEach(function (value: string) {
        const index = value.indexOf('_');
        matchedKeys.push(value.slice(0, index));
      });

      this.expandedKeys = matchedKeys.filter(function (item, pos, self) {
        return self.indexOf(item) === pos;
      });
    }

    this.changed.emit(this.filters);
  }

  private markAllSupportersFromTeam(team: TeamsWithSupportersDto) {

    team.Supporters.forEach(supporter => {
      if (this.filters.SelectedSupporters.indexOf(supporter.Id) >= 0) {
        return;
      } else {
        this.filters.SelectedSupporters.push(supporter.Id);
      }
    });
  }

}
